import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { Modal, Button, CardMedia, Input, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { UploadMedia } from "../utils/mediaUpload";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useAddBudgetCategoryMutation,
  useLazyGetBudgeByIdQuery,
  useUpdateBudgetCategoryMutation,
} from "../services/budgetCategory";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onSuccess: () => void;
  categoryId?: number | null;
}

export default function AddCategory({
  open,
  onClose,
  setOpen,
  onSuccess,
  categoryId = null,
}: ModalProps) {
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageId, setImageId] = useState<number | null>(null);
  const [addCategory] = useAddBudgetCategoryMutation();
  const [updateCategory] = useUpdateBudgetCategoryMutation();
  const [getById] = useLazyGetBudgeByIdQuery();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      category_name: "",
      threshold: "",
      icon: "",
    },
    validationSchema: Yup.object({
      category_name: Yup.string()
        .trim()
        .required("Category Name is required")
        .length(30, "Category Name cannot exceed 30 characters"),
      threshold: Yup.number()
        .min(1, "Threshold must be at least 1%")
        .max(100, "Threshold cannot exceed 100%")
        .required("Threshold is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const body = {
        category_name: values.category_name,
        threshold: values.threshold,
        icon: imageId,
      } as any;
      onSuccess();

      let isSuccess = false;
      try {
        if (categoryId) {
          const response = await updateCategory({
            id: categoryId,
            body,
          }).unwrap();
          if (
            (response as any)?.status === 200 ||
            (response as any)?.statusCode === 200
          ) {
            onSuccess();
            isSuccess = true;
          }
        } else {
          const response = await addCategory({ body }).unwrap();
          if (response?.status === 200 || response?.status === 201) {
            onSuccess();
            isSuccess = true;
            setOpen(false);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (isSuccess) {
          resetForm();
          setImageUrl("");
          setImageId(null);
        }
      }
    },
  });
  // Handle image upload
  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const response = (await UploadMedia(file)) as any;
    if (response?.data?.length > 0) {
      const uploaded = response.data[0];
      setImageUrl(uploaded.media_file_url);
      setImageId(uploaded.id);
    }
  };
  useEffect(() => {
    const fetchAndPrefill = async () => {
      if (!open) return;
      if (categoryId) {
        try {
          const response = await getById({ id: categoryId }).unwrap();
          const data = (response as any)?.data ?? (response as any) ?? {};
          formik.setValues({
            category_name: data.category_name || "",
            threshold: data.threshold ?? "",
            icon: data.icon ?? "",
          });
          if (data && typeof data.icon === "number") {
            setImageId(data.icon);
          } else if (data?.icon?.id) {
            setImageId(data.icon.id);
          }
          if (data?.icon?.media_file_url) {
            setImageUrl(data.icon.media_file_url);
          }
        } catch (e) {
          console.log(e, "Error");
        }
      } else {
        formik.resetForm();
        setImageUrl("");
        setImageId(null);
      }
    };
    fetchAndPrefill();
  }, [open, categoryId]);

  return (
    <Modal className="modal addCategory_modal" open={open} onClose={onClose}>
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{categoryId ? "Edit Category" : "Add Category"}</h2>
            <div className="btn-close" onClick={() => setOpen(false)}>
              <CloseIcon />
            </div>
          </div>

          <form className="form" onSubmit={formik.handleSubmit}>
            {/* Upload Image */}
            <div className="control_group">
              <label>Upload Category Image</label>
              {imageUrl ? (
                <div className="upload_image no_radius">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={imageUrl}
                        alt="category"
                      />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImageUrl("");
                        setImageId(null);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label
                  className="upload_image no_radius"
                  htmlFor="icon-button-file"
                >
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{ accept: "image/png,image/jpeg" }}
                    onChange={handleImageUpload}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="icon" />
                    </figure>
                  </span>
                </label>
              )}
            </div>

            {/* Category Name */}
            <div className="control_group">
              <label>Category Name</label>
              <TextField
                name="category_name"
                value={formik.values.category_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.category_name &&
                  Boolean(formik.errors.category_name)
                }
                helperText={
                  formik.touched.category_name && formik.errors.category_name
                }
                placeholder="Enter Category Name"
                fullWidth
                inputProps={{ maxLength: 30 }}
              />
            </div>

            {/* Threshold */}
            <div className="control_group">
              <label>Alert Threshold (%)</label>
              <TextField
                type="number"
                name="threshold"
                value={formik.values.threshold}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.threshold && Boolean(formik.errors.threshold)
                }
                helperText={formik.touched.threshold && formik.errors.threshold}
                placeholder="Enter Threshold (%)"
                inputProps={{ min: 1, max: 100, maxLength: 3 }}
                fullWidth
              />
            </div>

            {/* Footer */}
            <div className="footer_btn_flex">
              <Button className="btn btn_grey" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn btn_primary">
                {categoryId ? "Update" : "Submit"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
